import type { ReactNode } from 'react'
import { cn } from '@/internationalization/cn'

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info' | 'accent'

export interface BadgeProps {
  children: ReactNode
  tone?: BadgeTone
  /** 'solid' for a status that must be noticed; 'subtle' everywhere else. */
  variant?: 'subtle' | 'solid' | 'outline'
  size?: 'sm' | 'md'
  icon?: ReactNode
  /** A coloured dot instead of an icon — for live/processing states. */
  dot?: boolean
  className?: string
}

const SUBTLE: Record<BadgeTone, string> = {
  neutral: 'bg-surface-muted text-fg-muted',
  brand: 'bg-brand-subtle text-brand',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-info-subtle text-info',
  accent: 'bg-accent-subtle text-accent',
}

const SOLID: Record<BadgeTone, string> = {
  neutral: 'bg-fg-muted text-fg-inverted',
  brand: 'bg-brand text-brand-fg',
  success: 'bg-success text-white',
  warning: 'bg-warning text-white',
  danger: 'bg-danger text-white',
  info: 'bg-info text-white',
  accent: 'bg-accent text-white',
}

const DOT: Record<BadgeTone, string> = {
  neutral: 'bg-fg-subtle',
  brand: 'bg-brand',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  accent: 'bg-accent',
}

/**
 * A label for state, never for an action. If it is clickable it is a Button or
 * a Tag, not a Badge.
 */
export function Badge({
  children,
  tone = 'neutral',
  variant = 'subtle',
  size = 'sm',
  icon,
  dot,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
        size === 'sm' ? 'h-5 px-2 text-2xs' : 'h-6 px-2.5 text-xs',
        variant === 'solid' && SOLID[tone],
        variant === 'subtle' && SUBTLE[tone],
        variant === 'outline' && cn('border bg-transparent', SUBTLE[tone], 'bg-transparent'),
        className,
      )}
    >
      {dot && <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', DOT[tone])} />}
      {icon && <span aria-hidden className="[&>svg]:h-3 [&>svg]:w-3">{icon}</span>}
      {children}
    </span>
  )
}
